// src/components/credit/CreditLocationConsent.tsx
import React, { useState } from 'react';
import { MapPin, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/Button';

interface CreditLocationConsentProps {
  onLocationResult: (result: {
    outcome: 'granted' | 'denied' | 'not_requested';
    coords?: { latitude: number; longitude: number; accuracy: number };
  }) => void;
}

export const CreditLocationConsent: React.FC<CreditLocationConsentProps> = ({ onLocationResult }) => {
  const [status, setStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied'>('idle');

  const handleShareLocation = () => {
    if (!navigator.geolocation) {
      setStatus('denied');
      onLocationResult({ outcome: 'denied' });
      return;
    }

    setStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setStatus('granted');
        onLocationResult({
          outcome: 'granted',
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
        });
      },
      () => {
        setStatus('denied');
        onLocationResult({ outcome: 'denied' });
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const handleSkip = () => {
    setStatus('denied');
    onLocationResult({ outcome: 'not_requested' });
  };

  if (status === 'granted') {
    return (
      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-xs">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        <span>Approximate device location attached to verification record.</span>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 text-xs">
        <ShieldAlert className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <span>Precise device location not provided (network IP evidence will be used instead).</span>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
      <div className="flex items-start gap-3">
        <MapPin className="w-5 h-5 text-[#0f5a2e] mt-0.5 flex-shrink-0" />
        <div className="text-xs">
          <div className="font-semibold text-slate-900">Optional Submission Location Evidence</div>
          <p className="text-slate-600 mt-1">
            Would you like to share your current device location as additional verification evidence?
            This is completely optional and you may continue without sharing your precise coordinates.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          onClick={handleShareLocation}
          disabled={status === 'requesting'}
          className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white text-xs"
        >
          {status === 'requesting' ? 'Locating...' : 'Share My Location'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSkip}
          className="text-slate-600 text-xs"
        >
          Continue Without Location
        </Button>
      </div>
    </div>
  );
};
